import { Component } from '@theme/component';

/**
 * A lightbox with previous/next navigation between a fixed set of slides.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} slide - The slide elements.
 * @property {HTMLElement[]} [counter] - The "x / y" counter elements (one per slide).
 *
 * @extends Component<Refs>
 */
export class PortfolioLightbox extends Component {
  requiredRefs = ['dialog', 'slide'];

  #index = 0;
  #touchX = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.addEventListener('keydown', this.#handleKeyDown);
    this.refs.dialog.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.refs.dialog.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('cancel', this.#handleCancel);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.refs.dialog.removeEventListener('touchstart', this.#handleTouchStart);
    this.refs.dialog.removeEventListener('touchend', this.#handleTouchEnd);
  }

  /**
   * Opens the lightbox at a given slide index.
   *
   * @param {number} index - The index of the slide to show.
   */
  open(index) {
    this.#index = Number(index) || 0;
    this.#show();
    this.refs.dialog.showModal();
  }

  /**
   * Closes the lightbox, with an outro animation.
   */
  close() {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    dialog.classList.add('dialog-closing');
    dialog.addEventListener(
      'animationend',
      () => {
        dialog.classList.remove('dialog-closing');
        dialog.close();
      },
      { once: true }
    );
  }

  /**
   * Shows the previous slide, wrapping around to the last slide if needed.
   */
  prev() {
    const { slide } = this.refs;
    this.#index = (this.#index - 1 + slide.length) % slide.length;
    this.#show();
  }

  /**
   * Shows the next slide, wrapping around to the first slide if needed.
   */
  next() {
    const { slide } = this.refs;
    this.#index = (this.#index + 1) % slide.length;
    this.#show();
  }

  /**
   * Closes the lightbox when the backdrop (not its content) is clicked.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  handleBackdropClick(event) {
    if (event.target === this.refs.dialog) this.close();
  }

  #show() {
    const { slide, counter } = this.refs;

    slide.forEach((element, i) => {
      element.hidden = i !== this.#index;
    });

    if (counter) {
      const text = `${this.#index + 1} / ${slide.length}`;
      counter.forEach((element) => {
        element.textContent = text;
      });
    }
  }

  /**
   * @param {Event} event - The native dialog "cancel" event, fired on Escape.
   */
  #handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /**
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (!this.refs.dialog.open) return;

    if (event.key === 'ArrowLeft') this.prev();
    if (event.key === 'ArrowRight') this.next();
  };

  /**
   * @param {TouchEvent} event - The touchstart event.
   */
  #handleTouchStart = (event) => {
    this.#touchX = event.touches[0]?.clientX ?? null;
  };

  /**
   * @param {TouchEvent} event - The touchend event.
   */
  #handleTouchEnd = (event) => {
    if (this.#touchX === null) return;

    const endX = event.changedTouches[0]?.clientX ?? this.#touchX;
    const diff = this.#touchX - endX;

    if (Math.abs(diff) > 40) {
      if (diff > 0) this.next();
      else this.prev();
    }

    this.#touchX = null;
  };
}

if (!customElements.get('portfolio-lightbox-component')) {
  customElements.define('portfolio-lightbox-component', PortfolioLightbox);
}
